import { useFormikContext } from 'formik';
import { AddressFormField } from 'modules/company/components/AddressField';
import { BillingEmailField } from 'modules/company/components/BillingEmailField';
import { CompanyNameField } from 'modules/company/components/CompanyNameField';
import { CompanySearchModalContent } from 'modules/company/components/CompanySearchModalContent';
import { CountryCodeField } from 'modules/company/components/CountryCodeField';
import { EstablishmentSelect } from 'modules/company/components/EstablishmentSelect';
import { LegalFormField } from 'modules/company/components/LegalFormField';
import { RegistrationNumberField } from 'modules/company/components/RegistrationNumberField';
import { VatNumberField } from 'modules/company/components/VatNumberField';
import { CountryCode } from 'modules/company/types/company.type';
import { CompanyFormFields } from 'modules/company/types/companyFormFields.type';
import { extractSiren } from 'modules/company/utils/extractSiren';
import { generateVatNumber } from 'modules/company/utils/generateVatNumber';
import { mapRegistryCompanyResponseToFormFields } from 'modules/company/utils/mappers';
import React, { FunctionComponent, useState } from 'react';
import { Button, Form, Icon, Modal } from 'semantic-ui-react';
import { formatAddress } from 'utils/format-address';
import { CompanyFormModalProps } from '../../../components/company-form-modal/company-form-modal';

type CompanyFormProps = CompanyFormModalProps & {
  mode: 'update' | 'create';
};

type Step = 'search' | 'edit';

type InseePrefilledFields = {
  name: boolean;
  registrationNumber: boolean;
  legalForm: boolean;
  address: boolean;
};

export const CompanyForm: FunctionComponent<CompanyFormProps> = ({
  open,
  onClose,
  billingEmailSuggestions,
  mode,
}) => {
  const {
    values,
    isSubmitting,
    errors,
    setFieldValue,
    handleSubmit,
    handleReset,
    resetForm,
    submitForm,
    setValues,
  } = useFormikContext<CompanyFormFields>();

  const isFrUpdate = mode === 'update' && values.countryCode === CountryCode.FR;

  const [step, setStep] = useState<Step>(mode === 'create' ? 'search' : 'edit');

  const [isInseeDataLoaded, setIsInseeDataLoaded] = useState(false);

  const [inseePrefilledFields, setInseePrefilledFields] =
    useState<InseePrefilledFields>({
      name: false,
      registrationNumber: false,
      legalForm: false,
      address: false,
    });

  const isFieldDisabled = ({
    isPrefilledByInsee,
  }: {
    isPrefilledByInsee: boolean;
  }) => {
    if (mode === 'update') {
      return !isInseeDataLoaded || isPrefilledByInsee;
    }

    return isInseeDataLoaded && isPrefilledByInsee;
  };

  return (
    <>
      <Modal
        open={open}
        onClose={(): void => {
          if (onClose) {
            onClose();
          }
        }}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        closeIcon
      >
        <Modal.Header>
          {mode === 'update'
            ? "Édition de l'entreprise"
            : `Nouvelle entreprise`}
        </Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleSubmit} onReset={handleReset}>
            {step === 'search' ? (
              <CompanySearchModalContent
                onSelectResult={(result) => {
                  const foundData =
                    mapRegistryCompanyResponseToFormFields(result);

                  const vatNumber =
                    generateVatNumber({
                      registrationNumber: result.siret,
                      countryCode: CountryCode.FR,
                    }) ?? '';
                  setValues({
                    ...values,
                    ...foundData,
                    vatNumber,
                  });

                  setInseePrefilledFields({
                    name: !!foundData.name,
                    registrationNumber: !!foundData.registrationNumber,
                    legalForm: !!foundData.legalForm,
                    address:
                      !!foundData.address.streetAddress &&
                      !!foundData.address.postalCode &&
                      !!foundData.address.city,
                  });

                  setIsInseeDataLoaded(true);
                  setStep('edit');
                }}
                onManualEntrySelected={() => {
                  setStep('edit');

                  if (isInseeDataLoaded) {
                    resetForm();
                    setInseePrefilledFields({
                      name: false,
                      registrationNumber: false,
                      legalForm: false,
                      address: false,
                    });
                    setIsInseeDataLoaded(false);
                  }
                }}
              />
            ) : null}
            {step === 'edit' ? (
              <>
                <CountryCodeField
                  isDisabled={mode === 'update' || isInseeDataLoaded}
                  helpText={
                    mode === 'create' ? (
                      <span
                        style={{
                          color:
                            values.countryCode !== CountryCode.FR
                              ? 'red'
                              : undefined,
                        }}
                      >
                        Attention : une entreprise étrangère peut avoir un
                        établissement (SIRET) en France. Dans ce cas,
                        sélectionnez &quot;France&quot;.
                      </span>
                    ) : undefined
                  }
                />
                <CompanyNameField
                  isDisabled={isFieldDisabled({
                    isPrefilledByInsee: inseePrefilledFields.name,
                  })}
                />
                <Form.Group widths="equal">
                  <RegistrationNumberField
                    isDisabled={isFieldDisabled({
                      isPrefilledByInsee:
                        inseePrefilledFields.registrationNumber,
                    })}
                    countryCode={values.countryCode}
                    onBlur={() => {
                      const vatNumber = generateVatNumber({
                        registrationNumber: values.registrationNumber,
                        countryCode: values.countryCode,
                      });
                      if (vatNumber) {
                        setFieldValue('vatNumber', vatNumber);
                      }
                    }}
                  />
                  {isFrUpdate && (
                    <EstablishmentSelect
                      siren={extractSiren(values.registrationNumber)}
                      currentSiret={values.registrationNumber}
                      style={{ marginTop: '42px', maxHeight: '34px' }}
                      onSelect={(establishment) => {
                        setValues({
                          ...values,
                          registrationNumber: establishment.siret,
                          address: establishment.address,
                          formattedAddress: formatAddress({
                            street: establishment.address.streetAddress,
                            postalCode: establishment.address.postalCode,
                            city: establishment.address.city,
                          }),
                        });

                        setInseePrefilledFields({
                          name: true,
                          legalForm: true,
                          registrationNumber: !!establishment.siret,
                          address:
                            !!establishment.address.streetAddress &&
                            !!establishment.address.postalCode &&
                            !!establishment.address.city,
                        });

                        setIsInseeDataLoaded(true);
                      }}
                    />
                  )}
                </Form.Group>
                <LegalFormField
                  isDisabled={isFieldDisabled({
                    isPrefilledByInsee: inseePrefilledFields.legalForm,
                  })}
                />
                <VatNumberField />
                <AddressFormField
                  label={
                    isFrUpdate
                      ? 'Adresse (adresse attachée au SIRET)'
                      : undefined
                  }
                  disabled={isFieldDisabled({
                    isPrefilledByInsee: inseePrefilledFields.address,
                  })}
                />
                <BillingEmailField suggestions={billingEmailSuggestions} />
              </>
            ) : null}
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={(): void => {
              if (onClose) {
                onClose();
              }
              setStep('search');
            }}
          >
            Fermer
          </Button>

          {mode === 'create' && step === 'edit' ? (
            <Button
              onClick={(): void => {
                setStep('search');
              }}
            >
              Retour
            </Button>
          ) : null}

          {mode === 'create' && step === 'search' ? (
            <Button
              disabled={
                errors.formattedAddress ||
                errors.address?.streetAddress ||
                errors.address?.postalCode ||
                errors.address?.city ||
                errors.registrationNumber ||
                errors.name ||
                errors.vatNumber
                  ? true
                  : false
              }
              onClick={(): void => {
                setStep('edit');
              }}
              primary
            >
              Suivant
            </Button>
          ) : null}

          {step === 'edit' ? (
            <Button
              primary
              loading={isSubmitting}
              disabled={isSubmitting}
              type="submit"
              onClick={submitForm}
            >
              <Icon name="save" />
              Enregistrer
            </Button>
          ) : null}
        </Modal.Actions>
      </Modal>
    </>
  );
};
